// src/components/Dashboard.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Select,
    MenuItem,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Button,
    Switch,
    TextField, Paper,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { useSettings } from "../context/SettingsContext";

function Dashboard() {
    const {settings, setSettings} = useSettings();
    const [courses, setCourses] = useState([]);
    const [listeForce, setListeForce] = useState([]);
    const [error, setError] = useState(null);
    const [lastImportedCourse, setLastImportedCourse] = useState(null);

    // ✅ Filtres pour la liste des courses
    const [courseFilter, setCourseFilter] = useState("");
    const [courseDisciplineFilter, setCourseDisciplineFilter] = useState([]);
    useEffect(() => {
        // Charger les courses
        fetch("/courses")
            .then((res) => {
                if (!res.ok) throw new Error("server");
                return res.json();
            }).then((data) => {
            setCourses(data);
            if (data.length === 0) {
                setError("aucune course a été importée");
            } else {
                setError(null); // Déterminer la dernière course importée
                const last = data.reduce((a, b) => new Date(a.importDate) > new Date(b.importDate) ? a : b);
                setLastImportedCourse(last);
            }
        }).catch(() => {
            setError("pas de connexion avec le serveur, liste de force non disponible");
        });
        // Charger la liste de force
        fetch("/listeforce")
            .then((res) => {
                if (!res.ok) throw new Error("server");
                return res.json();
            }).then(setListeForce).catch(() => {
            setError("pas de connexion avec le serveur, liste de force non disponible");
        });
    }, []);

    // ✅ Fonction qui retourne les courses filtrées
    const getFilteredCourses = () => {
        return courses.filter((c) => {
            const text = `${c.codex} ${c.event} ${c.discipline}`.toLowerCase();
            if (!text.includes(courseFilter)) return false;

            if (
                courseDisciplineFilter.length > 0 &&
                !courseDisciplineFilter.includes(c.discipline)
            ) {
                return false;
            }

            return true;
        });
    };

    // === Sélection des courses ===
    const handleCourseToggle = (codex) => {
        const current = settings.selectedCodex || [];
        const exists = current.includes(codex);
        const updated = exists
            ? current.filter((c) => c !== codex)
            : [...current, codex];
        setSettings({...settings, selectedCodex: updated});
    };

    // ✅ Sélectionner uniquement les courses filtrées
    const handleSelectAll = () => {
        const filtered = getFilteredCourses();
        const codexList = filtered.map((c) => String(c.codex));

        const updated = Array.from(
            new Set([...(settings.selectedCodex || []), ...codexList])
        );

        setSettings({...settings, selectedCodex: updated});
    };

    // ✅ Désélectionner uniquement les courses filtrées
    const handleClearAll = () => {
        const filtered = getFilteredCourses();
        const codexList = filtered.map((c) => String(c.codex));

        const updated = (settings.selectedCodex || []).filter(
            (c) => !codexList.includes(c)
        );

        setSettings({...settings, selectedCodex: updated});
    };

    // === Sélection des disciplines BestX ===
    const toggleDiscipline = (d) => {
        const list = settings.selectedDisciplines.includes(d)
            ? settings.selectedDisciplines.filter((x) => x !== d)
            : [...settings.selectedDisciplines, d];

        setSettings({...settings, selectedDisciplines: list});
    };

    // ✅ Mise à jour BestX par discipline
    const updateCalcPerDiscipline = (discipline, value) => {
        setSettings({
            ...settings,
            calcPerDiscipline: {
                ...settings.calcPerDiscipline,
                [discipline]: value,
            },
        });
    };
    return (
        <Box sx={{padding: 2}}>
            <Typography variant="h5">Dashboard – Paramètres</Typography>

            {/* 🔴 Message d’erreur : aucune course importée */}
            {error === "aucune course a été importée" && (
                <Paper
                    sx={{
                        padding: 2,
                        backgroundColor: "#fff3e0",
                        color: "#e65100",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 2,
                    }}
                >
                    <WarningAmberIcon/>
                    <Typography>Aucune course n’a été importée.</Typography>
                </Paper>
            )}

            {/* 🔴 Message d’erreur : pas de connexion serveur */}
            {error === "pas de connexion avec le serveur, liste de force non disponible" && (
                <Paper
                    sx={{
                        padding: 2,
                        backgroundColor: "#ffebee",
                        color: "#b71c1c",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 2,
                    }}
                >
                    <ErrorIcon/>
                    <Typography>
                        Pas de connexion avec le serveur, liste de force non disponible.
                    </Typography>
                </Paper>
            )}

            {/* 🔵 Dernière course importée */}
            {!error && lastImportedCourse && (
                <Paper
                    sx={{
                        padding: 2,
                        backgroundColor: "#e3f2fd",
                        color: "#0d47a1",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        marginTop: 2,
                    }}
                >
                    <InfoIcon/>
                    <Box>
                        <Typography>
                            Dernière course importée : {lastImportedCourse.codex} –{" "}
                            {lastImportedCourse.event} ({lastImportedCourse.discipline})
                        </Typography>

                    </Box>
                </Paper>
            )}

            {/* Mode global / par discipline */}
            <Box sx={{marginTop: 3}}>
                <Typography variant="h6">Mode de calcul :</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={settings.calcModeType === "per-discipline"}
                            onChange={(e) =>
                                setSettings({
                                    ...settings,
                                    calcModeType: e.target.checked
                                        ? "per-discipline"
                                        : "global",
                                })
                            }
                        />
                    }
                    label={
                        settings.calcModeType === "per-discipline"
                            ? "Par discipline"
                            : "Global"
                    }
                />
            </Box>

            {/* Mode global */}
            {settings.calcModeType === "global" && (
                <Box sx={{marginTop: 2}}>
                    <Typography>Choix du calcul global :</Typography>
                    <Select
                        value={settings.calcMode}
                        onChange={(e) =>
                            setSettings({...settings, calcMode: e.target.value})
                        }
                    >
                        <MenuItem value="best1">Meilleur résultat</MenuItem>
                        <MenuItem value="best2">2 meilleurs</MenuItem>
                        <MenuItem value="best3">3 meilleurs</MenuItem>
                    </Select>
                </Box>
            )}

            {/* Mode par discipline */}
            {settings.calcModeType === "per-discipline" && (
                <Box sx={{marginTop: 2}}>
                    <Typography variant="h6">Calcul par discipline :</Typography>

                    {["SL", "GS", "SG", "CR"].map((d) => (
                        <Box
                            key={d}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                marginTop: 1,
                            }}
                        >
                            <Typography sx={{width: 40}}>{d}</Typography>

                            <Select
                                value={settings.calcPerDiscipline?.[d] || "best1"}
                                onChange={(e) => updateCalcPerDiscipline(d, e.target.value)}
                                size="small"
                            >
                                <MenuItem value="best1">Meilleur résultat</MenuItem>
                                <MenuItem value="best2">2 meilleurs</MenuItem>
                                <MenuItem value="best3">3 meilleurs</MenuItem>
                            </Select>
                        </Box>
                    ))}
                </Box>
            )}

            {/* Sélection des disciplines BestX */}
            <Box sx={{marginTop: 3}}>
                <Typography variant="h6">Disciplines à inclure :</Typography>

                <FormGroup>
                    {["SL", "GS", "SG", "CR"].map((d) => (
                        <FormControlLabel
                            key={d}
                            control={
                                <Checkbox
                                    checked={settings.selectedDisciplines.includes(d)}
                                    onChange={() => toggleDiscipline(d)}
                                />
                            }
                            label={d}
                        />
                    ))}
                </FormGroup>
            </Box>

            {/* Filtre catégorie */}
            <Box sx={{marginTop: 3}}>
                <Typography>Filtrer par catégorie :</Typography>
                <Select
                    value={settings.category}
                    onChange={(e) =>
                        setSettings({...settings, category: e.target.value})
                    }
                >
                    <MenuItem value="MU12">MU12</MenuItem>
                    <MenuItem value="MU14">MU14</MenuItem>
                    <MenuItem value="MU16">MU16</MenuItem>
                    <MenuItem value="WU12">WU12</MenuItem>
                    <MenuItem value="WU14">WU14</MenuItem>
                    <MenuItem value="WU16">WU16</MenuItem>
                </Select>
            </Box>

            {/* Sélection des courses */}
            <Box sx={{marginTop: 4}}>
                <Typography variant="h6">Sélectionner les courses à inclure :</Typography>

                {/* Filtre texte */}
                <TextField
                    fullWidth
                    label="Filtrer les courses (ex: Filles U14, SL, GS, 1250...)"
                    variant="outlined"
                    size="small"
                    sx={{marginTop: 2, marginBottom: 2}}
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value.toLowerCase())}
                />

                {/* Filtre par discipline */}
                <Typography variant="subtitle1">Filtrer les courses par discipline :</Typography>
                <FormGroup row sx={{marginBottom: 2}}>
                    {["SL", "GS", "SG", "CR"].map((d) => (
                        <FormControlLabel
                            key={d}
                            control={
                                <Checkbox
                                    checked={courseDisciplineFilter.includes(d)}
                                    onChange={() =>
                                        setCourseDisciplineFilter((prev) =>
                                            prev.includes(d)
                                                ? prev.filter((x) => x !== d)
                                                : [...prev, d]
                                        )
                                    }
                                />
                            }
                            label={d}
                        />
                    ))}
                </FormGroup>

                {/* Boutons tout sélectionner / tout désélectionner */}
                <Box sx={{display: "flex", gap: 2, marginBottom: 2}}>
                    <Button variant="outlined" onClick={handleSelectAll}>
                        Tout sélectionner (filtré)
                    </Button>
                    <Button variant="outlined" onClick={handleClearAll}>
                        Tout désélectionner (filtré)
                    </Button>
                </Box>

                {/* Liste filtrée des courses */}
                <FormGroup>
                    {getFilteredCourses().map((c) => (
                        <FormControlLabel
                            key={c.codex}
                            control={
                                <Checkbox
                                    checked={
                                        settings.selectedCodex?.includes(String(c.codex)) || false
                                    }
                                    onChange={() => handleCourseToggle(String(c.codex))}
                                />
                            }
                            label={`${c.codex} – ${c.event} (${c.discipline})`}
                        />
                    ))}
                </FormGroup>
            </Box>
        </Box>
    );
}

export default Dashboard;